<template>
  <div class="wound-treatments">
    <!--
     The latestTreatment() method can be CPU intense, and was being accessed many times
     in the section below. So this "loop" is a trick to only run the latestTreatment()
     once per wound. There's only one item in the array, so it only renders once, but
     it allows the use of the "treatment" value to be used without additional overhead.
    -->
    <div
      v-for="(item, index) in generatedWoundList"
      :key="item.id"
    >
      <div
        v-for="(treatment) in [latestTreatment(item)]"
        :key="treatment.id"
        class="wound"
        :class="treatmentIsValidated(
          treatment,
          item.practice_type_id !== 5 ? item.location.location_text : null,
          item.practice_type_id
        ) === false ? 'not-validated' : ''"
      >
        <v-card-actions>
          <v-icon
            color="error"
            class="delete-icon mr-3"
            @click="deleteWoundRequest(item.id)"
          >
            {{ icons.mdiDelete }}
          </v-icon>

          <btn
            v-if="isNewTreatment(treatment)"
            :label="isSigned ? 'View' : 'Edit'"
            :color="isSigned ? 'primary' : (treatmentIsValidated(
              treatment,
              item.practice_type_id !== 5 ? item.location.location_text : null,
              item.practice_type_id
            ) ? 'success' : 'error')"
            :icon="isSigned ? icons.mdiClipboardText : icons.mdiClipboardEdit"
            @click="editTreatment(item)"
          ></btn>

          <btn
            v-else
            label="Add"
            color="warning"
            :icon="icons.mdiClipboardPlus"
            :disabled="isSigned"
            @click="addTreatment(item)"
          ></btn>

          <v-spacer></v-spacer>

          <!-- current treatment - validated -->
          <v-icon
            v-if="isNewTreatment(treatment) && treatmentIsValidated(
              treatment,
              item.practice_type_id !== 5 ? item.location.location_text : null,
              item.practice_type_id
            )"
            large
            color="success"
            class="mr-3"
          >
            {{ icons.mdiCheckDecagram }}
          </v-icon>

          <!-- current treatment - NOT validated -->
          <v-icon
            v-if="isNewTreatment(treatment) && !treatmentIsValidated(
              treatment,
              item.practice_type_id !== 5 ? item.location.location_text : null,
              item.practice_type_id
            )"
            large
            color="error"
            class="mr-3"
          >
            {{ icons.mdiAlertDecagram }}
          </v-icon>

          <!-- previous treatment -->
          <v-icon
            v-if="!isNewTreatment(treatment)"
            large
            color="warning"
            class="mr-3"
          >
            {{ icons.mdiClipboardAlert }}
          </v-icon>

          <div
            class="practice-type-title"
          >
            #{{ item.wound_number }} -
            {{ $store.getters['baseData/practiceTypeFromId'](item.practice_type_id).title }}
          </div>

          <v-icon
            v-if=" item.practice_type_id === 1 && treatment.wound_treatment && treatment.wound_treatment.infection_signs && treatment.wound_treatment.infection_signs.some(x => x.title !== 'None')"
            large
            color="error"
            class="ml-3"
          >
            {{ icons.mdiBiohazard }}
          </v-icon>

          <v-spacer></v-spacer>
          <div
            class="text-center mr-3"
          >
            <v-menu
              v-if="mergedWounds.findIndex(i => i.generatedId === item.id) === -1"
              offset-y
            >
              <template
                v-slot:activator="{ on, attrs }"
              >
                <btn
                  :disabled="!isNewTreatment(treatment)"
                  color="warning"
                  dark
                  :icon="icons.mdiCallMerge"
                  v-bind="attrs"
                  v-on="on"
                >
                  Merge
                </btn>
              </template>
              <v-list>
                <v-list-item-group
                  v-model="mergeSelectedOption"
                  color="primary"
                >
                  <v-list-item
                    v-for="(listItem, listIndex) in mergeSelectOptions.filter( wound => wound.practice_type_id === item.practice_type_id)"
                    :key="listIndex"
                    @click="mergeTreatmentDialog(listItem, item)"
                  >
                    <v-list-item-title>{{ listItem.title }}</v-list-item-title>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-menu>
            <chip
              v-else
              color="success"
              :icon="icons.mdiCheckDecagram"
              :label="true"
              class="merged-text"
            >
              Merged
            </chip>
          </div>
        </v-card-actions>

        <div class="wound-treatment-details">
          <!-- This tells Vue the wound treatment isn't validated, so the form isn't validated -->
          <checkbox
            v-if="treatmentIsValidated(
              treatment,
              item.practice_type_id !== 5 ? item.location.location_text : null,
              item.practice_type_id
            ) === false"
            required
            style="display: none"
          ></checkbox>

          <v-row>
            <div class="pl-2 col-sm-2 ">
              Status:
            </div>
            <div :class="`col-sm-10 ${isNewTreatment(treatment) ? '' : 'warning--text'}`">
              {{ formattedStatus(treatment) }}
            </div>
          </v-row>

          <v-row v-if="item.practice_type_id !== 5">
            <div class="pl-2 col-sm-2 ">
              Location:
            </div>
            <div class="col-sm-10">
              {{ formattedLocation(item) }}
            </div>
          </v-row>

          <v-row v-if="item.practice_type_id !== 5">
            <div class="pl-2 col-sm-2 ">
              Etiology:
            </div>
            <div class="col-sm-10">
              {{ formattedEtiology(treatment) }}
            </div>
          </v-row>

          <v-row v-if="item.practice_type_id !== 6">
            <div class="pl-2 col-sm-2 ">
              {{
                item.practice_type_id === 1
                  ? 'Size (LxWxD):'
                  : 'G-Tube Size (Fr):'
              }}
            </div>
            <div :class="`col-sm-10 ${isResolved(item) ? 'success--text' : ''}`">
              {{ formattedSize(item, treatment) }}
            </div>
          </v-row>

          <v-row v-if="item.practice_type_id === 1">
            <div class="pl-2 col-sm-2 ">
              Progress:
            </div>
            <div class="col-sm-10">
              {{ formattedProgress(item, treatment) }}
            </div>
          </v-row>

          <v-row>
            <div class="pl-2 col-sm-2 ">
              Treatment Summary:
            </div>
            <div class="col-sm-10">
              {{ formattedSummary(treatment) }}
            </div>
          </v-row>
        </div>
      </div>
    </div>

    <!-- wound detail entry modal -->
    <WoundDetail
      v-model="wound"
      :modal-state.sync="woundModalState"
      :patient="patient"
      :is-signed="false"
      :is-circulation-compromised="isCirculationCompromised"
      :encounter-id="encounterId"
      :encounter-visit-date="visitDate"
      :visit-location="encounter.visit_location"
      :quick-entry="false"
    ></WoundDetail>

    <!-- derm detail entry modal -->
    <DermDetail
      v-model="wound"
      :modal-state.sync="dermModalState"
      :patient="patient"
      :is-signed="false"
      :encounter-id="encounterId"
      :encounter-visit-date="visitDate"
      :therapeutic-anticoagulant="therapeuticAnticoagulant"
      :visit-location="encounter.visit_location"
      :quick-entry="false"
    ></DermDetail>

    <!-- g-tube management modal -->
    <GTubeDetail
      v-model="wound"
      :modal-state.sync="gTubeModalState"
      :patient="patient"
      :is-signed="false"
      :encounter-id="encounterId"
      :encounter-visit-date="visitDate"
      :quick-entry="false"
    ></GTubeDetail>
  </div>
</template>

<script>

import {
  mdiAlert,
  mdiAlertDecagram,
  mdiBiohazard,
  mdiCallMerge,
  mdiCheckDecagram,
  mdiClipboardAlert,
  mdiClipboardEdit,
  mdiClipboardPlus,
  mdiClipboardText,
  mdiClose,
  mdiDelete,
  mdiHexagram,
  mdiPencil,
  mdiTableRowPlusAfter,
  mdiTrashCan,
} from '@mdi/js'
import { v4 as uuidv4 } from 'uuid'
import { mergeIfEmpty } from '@/utils/mergeIfEmpty'

import DermDetail from '@/components/notes/wound-detail/treatments/DermDetail.vue'
import GTubeDetail from '@/components/notes/wound-detail/treatments/GTubeDetail.vue'
import WoundDetail from '@/components/notes/wound-detail/treatments/WoundDetail.vue'

export default {
  components: {
    WoundDetail, DermDetail, GTubeDetail,
  },
  props: {
    value: {
      type: Array,
      default: null,
    },
    encounterId: {
      type: String,
      default: null,
    },
    patientId: {
      type: String,
      default: null,
    },
    generatedWoundList: {
      type: Array,
      default: null,
    },
    visitDate: {
      type: String,
      default: null,
    },
    isCirculationCompromised: {
      type: Boolean,
      default: null,
    },
    mergedWoundsInitial: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      toggleSelectButton: true,
      modalState: false,
      woundModalState: false,
      dermModalState: false,
      gTubeModalState: false,
      wound: null,
      existingWounds: [...this.value],
      generatedWounds: [...this.generatedWoundList],
      mergedWounds: [...this.mergedWoundsInitial],
      mergeSelectedOption: null,
      icons: {
        mdiTableRowPlusAfter,
        mdiTrashCan,
        mdiClipboardEdit,
        mdiCheckDecagram,
        mdiAlertDecagram,
        mdiClipboardText,
        mdiClipboardPlus,
        mdiClipboardAlert,
        mdiAlert,
        mdiBiohazard,
        mdiHexagram,
        mdiClose,
        mdiDelete,
        mdiPencil,
        mdiCallMerge,
      },
      duplicateWounds: null,
      encounter: null,
      patient: null,
      isSigned: null,

      therapeuticAnticoagulant: false,

      calculatedLatestTreatment: null,
    }
  },
  computed: {
    mergeSelectOptions() {
      const options = []
      this.existingWounds.forEach(wound => {
        options.push({
          title: `#${wound.wound_number} - ${this.$store.getters['baseData/practiceTypeFromId'](wound.practice_type_id).title} ${wound.location?.location_text ? `: ${wound.location.location_text}` : ''}`,
          wound_id: wound.id,
          practice_type_id: wound.practice_type_id,
        })
      })

      return options
    },
  },
  watch: {
    mergedWoundsInitial: {
      deep: true,
      handler(newValue) {
        this.mergedWounds = [...this.$lodash.cloneDeep(newValue)]
      },
    },
    value: {
      deep: true,
      handler(newValue) {
        this.existingWounds = [...newValue]
      },
    },
    generatedWoundList: {
      deep: true,
      handler(newValue) {
        this.generatedWounds = [...newValue]
      },
    },
    woundModalState() {
      if (!this.woundModalState) {
        if (this.wound.treatments && this.wound.treatments.length === 0 && this.wound.can_be_deleted) {
          this.deleteWound()
        } else if (this.wound) {
          this.updateWoundTreatment()
        }
      }
    },
    dermModalState() {
      if (!this.dermModalState) {
        if (this.wound.treatments && this.wound.treatments.length === 0 && this.wound.can_be_deleted) {
          this.deleteWound()
        } else if (this.wound) {
          this.updateWoundTreatment()
        }
      }
    },
    gTubeModalState() {
      if (!this.gTubeModalState) {
        if (this.wound.treatments && this.wound.treatments.length === 0 && this.wound.can_be_deleted) {
          this.deleteWound()
        } else if (this.wound) {
          this.updateWoundTreatment()
        }
      }
    },
  },
  created() {
    this.encounter = this.$store.getters['encounters/getById'](this.encounterId)
    this.isSigned = this.encounter.is_signed
    this.patient = this.$store.getters['patients/getById'](this.patientId)
  },
  mounted() {
    this.mergeTreatmentByIds()
  },
  methods: {
    isResolved(wound) {
      if (!wound) return false

      if (Number(wound.practice_type_id) === 1) {
        return wound.is_healed
      }

      return false
    },
    isNewTreatment(treatment) {
      return (!treatment || !treatment.encounter) ? false : this.encounterId === treatment.encounter_id
    },
    latestTreatment(wound) {
      if (!wound.treatments.length) return false

      const treatment = wound.treatments.find(x => x.encounter_id === this.encounterId && !x.deleted)
      if (treatment) return treatment

      const treatments = wound.treatments.filter(x => !x.deleted && x.encounter.visit_date < this.visitDate).sort((a, b) => (a.encounter.visit_date < b.encounter.visit_date && 1) || -1)

      return (Array.isArray(treatments) && treatments.length) ? treatments[0] : false
    },
    formattedStatus(treatment) {
      if (!treatment || !treatment.encounter) return null

      if (this.encounterId === treatment.encounter_id) {
        return `Current encounter treatment ${this.$date(treatment.encounter.visit_date).format('M/D/YYYY')}`
      }

      return `Previous treatment date ${this.$date(treatment.encounter.visit_date).format('M/D/YYYY')}`
    },
    formattedLocation(wound) {
      return wound.location ? wound.location.location_text : null
    },
    formattedEtiology(treatment) {
      if (!treatment) return null

      const { etiology } = treatment

      return etiology === 'Other' ? `Other - ${treatment.etiology_other}` : etiology
    },
    formattedSize(wound, treatment) {
      if (!treatment) return null

      if (Number(wound.practice_type_id) === 1 && treatment.wound_treatment) {
        return treatment.wound_treatment.wound_treatment_wound_size.wound_size_text
      }
      if (Number(wound.practice_type_id) === 5 && treatment.g_tube_treatment) {
        return treatment.g_tube_treatment.replacement_size
      }

      return null
    },
    formattedProgress(wound, treatment) {
      if (!treatment || !treatment.wound_treatment) return null

      const { progress } = treatment.wound_treatment

      return progress === 'Other' ? `Other - ${treatment.wound_treatment.progress_other}` : progress
    },
    formattedSummary(treatment) {
      if (!treatment || !treatment.encounter) return null

      return this.$store.getters['baseData/procedureFromId'](treatment.procedure_id)
    },
    selectWound(wound) {
      this.wound = wound

      this.toggleModalTypeState(wound.practice_type_id)
    },
    toggleModalTypeState(practiceType) {
      switch (practiceType) {
        case 1:
          this.woundModalState = true
          break
        case 6:
          this.dermModalState = true
          break
        case 5:
          this.gTubeModalState = true
          break
        default:
      }

      // Mark encounter as changed to trigger discard prompt on accidental back navigation
      // this.$emit('update:encounterChanged', true)
    },
    deleteWound() {
      const index = this.generatedWounds.findIndex(x => x.id === this.wound.id)
      if (index !== -1 && this.wound.delete_wound && this.wound.can_be_deleted) {
        this.patient.all_attachments = this.patient.all_attachments.filter(a => a.wound_id !== this.wound.id)
        this.generatedWounds.splice(index, 1)
        this.$emit('update:generated-wound-list', [...this.generatedWounds])

        this.wound = null
      }
    },
    hasTreatmentUpdate() {
      // Update the hasTreatment value which indicates if there's any treatments for this encounter
      this.hasTreatment = false
      this.existingWounds.forEach(wound => {
        if (wound.treatments ? wound.treatments.findIndex(x => x.encounter_id === this.encounterId && !x.deleted) !== -1 : false) {
          this.hasTreatment = true
        }
      })
    },
    updateWoundTreatment() {
      // Find the index of the wound if it already exists or return -1
      const index = this.generatedWounds.length > 0
        ? this.generatedWounds.findIndex(x => x.id === this.wound.id)
        : -1
      // If the wound doesn't exist, we created a new wound by adding it to the end of the array
      // To trigger reactivity and ensure that Vue detects the change we use the spread operator on this.wound
      if (index === -1) this.generatedWounds.push({ ...this.wound })
      else {
        const existingWoundsIndex = this.existingWounds.length > 0
          ? this.existingWounds.findIndex(x => x.id === this.wound.id)
          : -1
        if (existingWoundsIndex !== -1) {
          this.existingWounds.splice(existingWoundsIndex, 1, { ...this.wound })
          this.$emit('input', [...this.existingWounds])
        }
        this.generatedWounds.splice(index, 1, { ...this.wound })
      }

      this.$emit('update:generated-wound-list', [...this.generatedWounds]) // Emitting a new array reference to reassure reactivity
      this.$emit('update:wound-treatment-updated', true)
      this.wound = null

      this.hasTreatmentUpdate()
    },
    treatmentIsValidated(treatment, woundLocation = null, practiceType) {
      if (!treatment || treatment.encounter_id !== this.encounterId) return true

      const hasValidLocation = typeof woundLocation === 'string' && woundLocation.trim().length > 0

      return treatment.is_validated && (practiceType === 5 || hasValidLocation)
    },
    deleteWoundRequest(woundId) {
      this.$root.confirm({
        title: 'Delete Wound and Treatment Record?',
        body: 'Are you sure you wish to delete this wound and treatment record?',
        alert: true,
        alertText: 'This operation cannot be undone!',
        alertColor: 'error',
        alertWarning: true,
        confirm: 'Delete',
        confirmIcon: this.icons.mdiDelete,
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          // Delete attachments related to the wound treatment
          const wound = this.generatedWoundList?.find(w => w.id === woundId)
          const treatment = wound?.treatments?.find(trt => trt?.wound_id === woundId)
          const treatmentId = treatment?.id ?? null
          if (treatmentId) this.patient.all_attachments = this.patient.all_attachments.filter(a => a.treatment_id !== treatmentId)
          this.patient.all_attachments = this.patient.all_attachments.filter(a => a.wound_id !== woundId)
          this.generatedWounds = this.generatedWounds.filter(w => w.id !== woundId)
          this.$emit('update:generated-wound-list', this.generatedWounds)
          this.$emit('update:wound-treatment-updated', true)
          this.hasTreatmentUpdate()
        }
      })
    },
    editTreatment(wound) {
      // Wound treatment form is quite large and there can be a rendering delay, so show the loading modal
      // if (wound.practice_type_id === 1) this.$store.dispatch('loading', true)
      clearTimeout(this.timeoutId)
      this.timeoutId = setTimeout(() => {
        this.wound = {
          ...wound,

          // Not in the DB, for Vue only
          // If there are treatments (other than the current treatment) that are
          // not marked as deleted and is validated, then consider this as a follow-up.
          followup_treatment: wound.treatments
            ? wound.treatments.filter(
              x => !x.deleted
                && x.is_validated
                && x.encounter_id !== this.encounterId,
            ).length > 0
            : false,
        }

        this.openDialog(wound.practice_type_id)
      }, 1)
    },
    openDialog(practiceType) {
      this.setLatestTreatmentFromPracticeType(practiceType)
      switch (practiceType) {
        case 1:
          this.woundModalState = true
          break
        case 6:
          this.dermModalState = true
          break
        case 5:
          this.gTubeModalState = true
          break
        default:
      }

      // Mark encounter as changed to trigger discard prompt on accidental back navigation
      this.$emit('update:encounterChanged', true)
    },
    mergeTreatmentDialog(selectedItem, item) {
      this.$root.confirm({
        title: 'Merge treatment?',
        body: `Are you sure you wish to merge this treatment with an existing wound?<br><br><strong>${selectedItem.title}</strong>`,
        cancel: 'Cancel',
        cancelColor: 'secondary',
        cancelIcon: this.icons.mdiLogoutVariant,
        html: true,
        confirm: 'Merge',
      }).then(result => {
        if (result === true) {
          this.mergeTreatment(selectedItem, item)

          this.$emit('input', [...this.existingWounds])

          this.mergeSelectedOption = null
        }
      })
    },
    mergeTreatment(selectedItem, item) {
      const existingWoundIndex = this.existingWounds.findIndex(wound => wound.id === selectedItem.wound_id)
      const existingTreatmentIndex = this.existingWounds[existingWoundIndex].treatments.findIndex(
        treatment => treatment.encounter_id === this.encounterId,
      )
      const generatedWoundIndex = this.generatedWoundList.findIndex(wound => wound.id === item.id)

      // If the existing wound has a new treatment, merge the existing treatment with the generated treatment,
      // push onto existing wound's treatment array otherwise
      if (this.isNewTreatment(this.existingWounds[existingWoundIndex].treatments[existingTreatmentIndex])) {
        const existingTreatment = this.$lodash.cloneDeep(this.existingWounds[existingWoundIndex].treatments[existingTreatmentIndex])
        const generatedTreatment = this.$lodash.cloneDeep(this.generatedWoundList[generatedWoundIndex].treatments[0])

        const mergedTreatment = mergeIfEmpty(existingTreatment, generatedTreatment)

        mergedTreatment.id = existingTreatment.id
        mergedTreatment.wound_id = existingTreatment.wound_id

        this.existingWounds[existingWoundIndex].treatments[existingTreatmentIndex] = mergedTreatment
      } else {
        this.existingWounds[existingWoundIndex].treatments.push(
          {
            ...this.generatedWoundList[generatedWoundIndex].treatments[0],
            wound_id: item.existingId,
          },
        )
      }

      this.generatedWounds[generatedWoundIndex] = this.$lodash.cloneDeep(this.existingWounds[existingWoundIndex])
      this.$emit('update:generated-wound-list', [...this.generatedWounds])
      this.mergedWounds.push({ generatedId: selectedItem.wound_id, existingId: selectedItem.wound_id })
      this.$emit('update:merged-wounds-initial', [...this.mergedWounds])
      this.$emit('input', [...this.existingWounds])
    },
    mergeTreatmentByIds() {
      this.mergedWounds.forEach((item, index) => {
        const existingWoundIndex = this.existingWounds.findIndex(wound => wound.id === item.existingId)
        const generatedWoundIndex = this.generatedWoundList.findIndex(wound => wound.id === item.generatedId)
        const existingTreatmentIndex = this.existingWounds[existingWoundIndex].treatments.findIndex(
          treatment => treatment.encounter_id === this.encounterId,
        )

        // If the existing wound has a new treatment, merge the existing treatment with the generated treatment,
        // push onto existing wound's treatment array otherwise
        if (this.isNewTreatment(this.existingWounds[existingWoundIndex].treatments[existingTreatmentIndex])) {
          const existingTreatment = this.$lodash.cloneDeep(this.existingWounds[existingWoundIndex].treatments[existingTreatmentIndex])
          const generatedTreatment = this.$lodash.cloneDeep(this.generatedWoundList[generatedWoundIndex].treatments[0])

          const mergedTreatment = mergeIfEmpty(existingTreatment, generatedTreatment)

          mergedTreatment.id = existingTreatment.id
          mergedTreatment.wound_id = existingTreatment.wound_id

          this.existingWounds[existingWoundIndex].treatments[existingTreatmentIndex] = mergedTreatment
          this.generatedWounds[generatedWoundIndex] = this.$lodash.cloneDeep(this.existingWounds[existingWoundIndex])
        } else {
          this.existingWounds[existingWoundIndex].treatments.push(
            {
              ...this.generatedWoundList[generatedWoundIndex].treatments[0],
              wound_id: item.existingId,
            },
          )
          this.generatedWounds[generatedWoundIndex] = this.$lodash.cloneDeep(this.existingWounds[existingWoundIndex])
        }
        this.mergedWounds[index].generatedId = this.existingWounds[existingWoundIndex].id
      })

      this.$emit('update:merged-wounds-initial', [...this.mergedWounds])
      this.$emit('input', [...this.existingWounds])
      this.$emit('update:generated-wound-list', [...this.generatedWounds])
    },
    setLatestTreatmentFromPracticeType(practiceTypeId) {
      let latestTreatments = this.$store.getters['patients/getLatestTreatments'](this.patient.id, this.visitDate, practiceTypeId, this.wound.id)

      if (!latestTreatments) {
        this.calculatedLatestTreatment = null

        return
      }

      // Filter only signed encounters only if the property is_signed doesn't exist on the treatment.encounter object
      if (latestTreatments && latestTreatments.some(trt => !(trt.hasOwnProperty('is_signed')))) {
        latestTreatments = latestTreatments.filter(latestTreatment => this.$store.getters['encounters/getById'](latestTreatment.encounter_id).is_signed)
      }

      this.calculatedLatestTreatment = latestTreatments[0]
    },
    async addTreatment(wound) {
      // Check if the wound is healed
      if (wound.is_healed) {
        // Show confirmation dialog
        const result = await this.$root.confirm({
          title: 'Confirm Treatment Addition',
          body: 'This wound is healed. Are you sure you want to add a treatment?',
          confirm: 'Add a treatment',
          cancel: 'Cancel',
        })
        if (!result) {
          return // Exit if the user did not confirm adding treatment to a healed wound.
        }
      }

      // Wound treatment form is quite large and there can be a rendering delay, so show the loading modal
      // if (wound.practice_type_id === 1) this.$store.dispatch('loading', true)
      clearTimeout(this.timeoutId)
      this.timeoutId = setTimeout(() => {
        const latestTreatment = this.latestTreatment(wound)

        // Changes here should also be duplicated in QuickEntry.vue, method `addTreatment`

        // Find a etiology match...
        let etiology = null
        let etiologyOther = null
        if (latestTreatment) {
          etiology = latestTreatment.etiology
          etiologyOther = latestTreatment.etiology_other
          if (latestTreatment.etiology && (wound.practice_type_id === 1 || wound.practice_type_id === 6)) {
            const etiologies = wound.practice_type_id === 1 ? this.woundEtiologies : this.dermEtiologies
            if (etiologies.findIndex(x => x.title === etiology) === -1) {
              const etiologyIndex = etiologies.findIndex(x => x.title === this.$custom.toProperCaseAll(latestTreatment.etiology))
              if (etiologyIndex > -1) {
                etiology = etiologies[etiologyIndex].title
              } else if (etiology && !etiologyOther) {
                etiologyOther = etiology
                etiology = 'Other'
              }
            }
          }
        }

        const newTreatmentId = uuidv4()
        const newTreatment = {
          id: newTreatmentId,
          place_of_service_id: this.placeOfServiceId,
          patient_id: this.patient.id,
          encounter_id: this.encounterId,
          wound_id: wound.id,
          etiology,
          etiology_other: etiologyOther,
          procedure_id: null,
          current_treatment: null,
          qent_notes: null,
          qent_recommendation: null,
          operative_note: null,
          is_validated: false,
          is_quick_note_validated: true,
          created: this.$custom.utcNow(),
          encounter: {
            id: this.encounterID,
            visit_date: this.visitDate,
          },

          // Not in the DB, for Vue only
          require_vitals: null,
          can_be_deleted: true,
          updated: true,
        }

        if (wound.practice_type_id === 1) {
          // Wound treatment
          newTreatment.wound_treatment = {
            wound_treatment_wound_size: {},
            qent_undermining: '',
            wound_description_summary: '',
            exudate_amounts: [],
            exudate_types: [],
          }

          // Pull in consent data from operative note wizard, mainly the consent info
          if (latestTreatment && latestTreatment.operative_note_wizard) {
            newTreatment.operative_note_wizard = {
              consent_selected: latestTreatment.operative_note_wizard.consent_selected,
              consent_text: latestTreatment.operative_note_wizard.consent_text,
            }
          }

          // Wound stage from pervious treatment carries forward
          if (latestTreatment) {
            const woundStage = latestTreatment?.wound_treatment?.wound_treatment_wound_size?.wound_stage
            if (woundStage) {
              newTreatment.wound_treatment.wound_treatment_wound_size.wound_stage = woundStage
            }
          }
        } else if (wound.practice_type_id === 6) {
          // Derm treatment
          newTreatment.derm_treatment = {}
        } else if (wound.practice_type_id === 5) {
          // G-Tube treatment
          newTreatment.g_tube_treatment = {}
        }

        wound.treatments.push(newTreatment)
        this.wound = {
          ...wound,

          // Not in the DB, for Vue only
          // If there are treatments (other than the current treatment) that are
          // not marked as deleted and is validated, then consider this as a follow-up.
          followup_treatment: wound.treatments
            ? wound.treatments.filter(
              x => !x.deleted
                && x.is_validated
                && x.encounter_id !== this.encounterId,
            ).length > 0
            : false,
          is_healed: false, // Mark the wound as not healed if the user adds a treatment
          updated: true,
        }
        this.openDialog(wound.practice_type_id)
      }, 1)
    },
  },
}
</script>

<style lang="scss">
.delete-icon {
  z-index: 1;
  right: 4px;
  left: 4px;
  padding: 4px !important;
  width: 44px;
  height: 44px;
  .v-icon__svg {
    width: 36px;
    height: 36px;
  }
}
.filters-title {
  margin-bottom: 8px;
}
.wound-treatments {
  min-height: 150px;
  // max-height: 600px;
  // overflow-y: scroll;
  > .row .v-card__actions {
    margin-bottom: 16px !important;
  }
  .wound {
    border: thin solid rgba(0, 0, 0, 0.14);
    border-radius: 6px;
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0 !important;
    margin-bottom: 20px;
    .v-card__actions {
      padding: 4px;
      margin: 0 !important;
      border-bottom: thin solid rgba(0, 0, 0, 0.14);
      button {
        margin: 0;
      }
      .practice-type-title {
        font-size: 16px;
        font-weight: 600;
        color: var(--v-secondary-darken3) !important;
        line-height: 2;
        padding: 0;
      }
    }
    .wound-treatment-details {
      margin: 0 !important;
      padding: 16px 4px;
      background-color: #fff;
      border-bottom-right-radius: 6px;
      border-bottom-left-radius: 6px;
      .row {
        margin-left: 0;
        margin-right: 0;
        label, > div {
          line-height: 22px;
        }
        label {
          padding: 5px 10px 3px 0;
          color: rgba(94, 86, 105, 0.68);
        }
        > div {
          font-size: 16px;
          padding: 4px 0;
          font-weight: 500;
          color: rgba(0, 0, 0, 0.68);
          &.warning--text {
            color: var(--v-warning-darken1) !important;
          }
        }
      }
    }
  }
}
</style>
